import React, { useState } from 'react';
import { Upload, Sparkles, Edit3, Link2, X, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { compressImage, formatBytes } from '../lib/imageCompression';
import { uploadToStorage } from '../lib/storageUpload';
import { supabase } from '../lib/supabase';
import { ImagePickerModal } from './ImagePickerModal';
import { useCreditsCheck, canAffordAction } from '../hooks/useCreditsCheck';
import { useCreditPricing } from '../hooks/useCreditPricing';

interface FeaturedImageManagerProps {
  currentImageUrl: string;
  userId: string;
  blogId: string;
  onImageChange: (url: string) => void;
}

export function FeaturedImageManager({
  currentImageUrl,
  userId,
  blogId,
  onImageChange,
}: FeaturedImageManagerProps) {
  const creditsInfo = useCreditsCheck(userId);
  const { pricing } = useCreditPricing();
  const imageGenerationCost = pricing['images_generate'] ?? 400;
  const canAffordImages = canAffordAction(creditsInfo, imageGenerationCost);
  const [mode, setMode] = useState<'upload' | 'ai-auto' | 'ai-custom' | 'url' | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [compressionInfo, setCompressionInfo] = useState<{
    originalSize: number;
    compressedSize: number;
    ratio: number;
  } | null>(null);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [showImagePicker, setShowImagePicker] = useState(false);

  const handleFileSelect = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      alert('Bestand is te groot. Maximale grootte is 10MB.');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsProcessing(true);

      const compressed = await compressImage(selectedFile, 1920, 0.5, 'webp', 0.85);

      setCompressionInfo({
        originalSize: compressed.originalSize,
        compressedSize: compressed.compressedSize,
        ratio: compressed.compressionRatio,
      });

      const result = await uploadToStorage(compressed.blob, userId, 'blog', { blogId });

      onImageChange(result.publicUrl);
      setMode(null);
      setSelectedFile(null);
      setUploadPreview(null);
      setCompressionInfo(null);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Er ging iets mis bij het uploaden. Probeer het opnieuw.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAIGenerate = async (useCustom: boolean) => {
    try {
      setIsProcessing(true);

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-generate`;
      const headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user_id: userId,
          published_blog_id: blogId,
          idea_title: '',
          idea_content: '',
          custom_prompt: useCustom ? customPrompt : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Leonardo API error:', errorData);
        throw new Error(errorData.error || 'Failed to generate image');
      }

      const data = await response.json();
      const imageUrls = data.image_urls || [];

      if (imageUrls.length > 0) {
        setGeneratedImages(imageUrls);
        setShowImagePicker(true);
        setIsProcessing(false);
      } else {
        throw new Error('Geen afbeeldingen gegenereerd');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      alert('Er ging iets mis bij het genereren. Probeer het opnieuw.');
      setIsProcessing(false);
    }
  };

  const handleImageSelection = async (selectedImageUrl: string) => {
    try {
      setShowImagePicker(false);
      setIsProcessing(true);

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      const downloadUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-and-store-image`;
      const headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      };

      const downloadResponse = await fetch(downloadUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          imageUrl: selectedImageUrl,
          userId,
          sessionId: blogId,
          context: 'blog',
          blogId,
        }),
      });

      if (!downloadResponse.ok) {
        const downloadError = await downloadResponse.json();
        console.error('Download and store error:', downloadError);
        throw new Error(downloadError.error || 'Failed to store image');
      }

      const { publicUrl } = await downloadResponse.json();
      onImageChange(publicUrl);
      setMode(null);
      setCustomPrompt('');
    } catch (error) {
      console.error('Image selection error:', error);
      alert('Er ging iets mis bij het opslaan van de afbeelding. Probeer het opnieuw.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelImagePicker = () => {
    setShowImagePicker(false);
    setGeneratedImages([]);
    setIsProcessing(false);
  };

  const handleExternalUrl = () => {
    if (!externalUrl.trim()) return;
    onImageChange(externalUrl);
    setMode(null);
    setExternalUrl('');
  };

  return (
    <>
      <ImagePickerModal
        images={generatedImages}
        isOpen={showImagePicker}
        onSelect={handleImageSelection}
        onCancel={handleCancelImagePicker}
        title="Kies je favoriete afbeelding"
        description="Selecteer de afbeelding die je als featured image wilt gebruiken"
      />

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">Featured Image</label>

      {currentImageUrl && (
        <div className="relative rounded-lg overflow-hidden border border-gray-200 shadow-sm">
          <img
            src={currentImageUrl}
            alt="Featured"
            className="w-full h-auto aspect-video object-cover"
            onError={(e) => {
              e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="450"%3E%3Crect fill="%23e5e7eb" width="800" height="450"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="18"%3EAfbeelding niet beschikbaar%3C/text%3E%3C/svg%3E';
            }}
          />
          {generatedImages.length > 0 && (
            <button
              onClick={() => setShowImagePicker(true)}
              className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-lg shadow-lg border border-gray-200 transition-all hover:scale-105 group"
              title="Kies andere variant"
            >
              <RefreshCw className="w-4 h-4 text-gray-700 group-hover:text-[#231F20]" />
            </button>
          )}
        </div>
      )}

      {!mode ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={() => setMode('upload')}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-300 rounded-lg hover:border-[#231F20] hover:bg-gray-50 transition-colors"
            >
              <Upload className="w-5 h-5" />
              <span className="font-medium">Upload</span>
            </button>

            <button
              onClick={() => canAffordImages && setMode('ai-auto')}
              disabled={!canAffordImages}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-300 rounded-lg hover:border-[#231F20] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:bg-white"
            >
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">AI Auto</span>
            </button>

            <button
              onClick={() => canAffordImages && setMode('ai-custom')}
              disabled={!canAffordImages}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-300 rounded-lg hover:border-[#231F20] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:bg-white"
            >
              <Edit3 className="w-5 h-5" />
              <span className="font-medium">Eigen prompt</span>
            </button>
          </div>

          {!canAffordImages && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-900 mb-1">
                    Onvoldoende credits voor AI afbeeldingen
                  </h4>
                  <p className="text-sm text-amber-800 mb-3">
                    AI afbeeldingen genereren kost {imageGenerationCost} credits. Je hebt momenteel {creditsInfo.total} credits.
                  </p>
                  <a
                    href="/dashboard/settings"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                  >
                    Credits kopen
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#231F20]">
              {mode === 'upload' && 'Upload Bestand'}
              {mode === 'ai-auto' && 'AI Automatisch Genereren'}
              {mode === 'ai-custom' && 'Eigen prompt'}
              {mode === 'url' && 'Externe URL'}
            </h3>
            <button
              onClick={() => {
                setMode(null);
                setSelectedFile(null);
                setUploadPreview(null);
                setCustomPrompt('');
                setExternalUrl('');
                setCompressionInfo(null);
              }}
              className="text-gray-500 hover:text-[#231F20]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {mode === 'upload' && (
            <>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#231F20] transition-colors cursor-pointer"
                onClick={() => document.getElementById('featured-upload')?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-[#231F20]', 'bg-gray-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-[#231F20]', 'bg-gray-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-[#231F20]', 'bg-gray-50');
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith('image/')) {
                    handleFileSelect(file);
                  }
                }}
              >
                {uploadPreview ? (
                  <div className="space-y-4">
                    <img src={uploadPreview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                    <p className="text-sm text-gray-600">
                      {selectedFile?.name} ({formatBytes(selectedFile?.size || 0)})
                    </p>
                    {compressionInfo && (
                      <div className="text-sm text-green-600">
                        Gecomprimeerd: {formatBytes(compressionInfo.originalSize)} → {formatBytes(compressionInfo.compressedSize)}
                        ({compressionInfo.ratio.toFixed(1)}% besparing)
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <p className="text-gray-600">
                      <span className="font-semibold text-[#231F20]">Klik om te uploaden</span> of sleep je afbeelding hier
                    </p>
                    <p className="text-sm text-gray-500">PNG, JPG, WebP tot 10MB</p>
                  </div>
                )}
              </div>

              <input
                id="featured-upload"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />

              {selectedFile && (
                <button
                  onClick={handleUpload}
                  disabled={isProcessing}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploaden...
                    </>
                  ) : (
                    'Opslaan'
                  )}
                </button>
              )}
            </>
          )}

          {mode === 'ai-auto' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                AI analyseert je blog content en genereert automatisch een geoptimaliseerde afbeelding
              </p>
              <button
                onClick={() => handleAIGenerate(false)}
                disabled={isProcessing}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Genereren...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Genereer afbeelding ({imageGenerationCost} credits)
                  </>
                )}
              </button>
            </div>
          )}

          {mode === 'ai-custom' && (
            <div className="space-y-4">
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Vrouw van 50 zit in een koffietent te typen op haar Macbook, natuurlijk licht van zijraam, gefotografeerd over haar schouder, gezicht niet zichtbaar, notitieblok en telefoon op tafel, wazige achtergrond met café-interieur, warme kleuren"
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent resize-none"
              />
              <button
                onClick={() => handleAIGenerate(true)}
                disabled={isProcessing || !customPrompt.trim()}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Genereren...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Genereer met prompt ({imageGenerationCost} credits)
                  </>
                )}
              </button>
            </div>
          )}

          {mode === 'url' && (
            <div className="space-y-4">
              <input
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://..."
                className="input-field"
              />
              <button
                onClick={handleExternalUrl}
                disabled={!externalUrl.trim()}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Gebruik URL
              </button>
            </div>
          )}
        </div>
      )}

        <button
          onClick={() => setMode(mode === 'url' ? null : 'url')}
          className="text-sm text-[#231F20] hover:underline flex items-center gap-1"
        >
          <Link2 className="w-4 h-4" />
          Of gebruik externe URL
        </button>
      </div>
    </>
  );
}